import React, { useState, useEffect } from 'react';

const CSVProcessingLoader = ({ onComplete, duration = 1000 }) => {
  const [stage, setStage] = useState('loading');
  const [statusText, setStatusText] = useState('Initializing CSV Processing...');

  useEffect(() => {
    const statusSequence = [
      { time: duration * 0.15, text: 'Reading data sources...' },              // ~15% through
      { time: duration * 0.55, text: 'Formatting data rows...' },              // ~55% through
      { time: duration * 0.75, text: 'Optimizing file structure...' },         // ~75% through
      { time: duration, text: 'Reporting now ready' }                     // 100%
    ];

    const timers = statusSequence.map(({ time, text }) =>
      setTimeout(() => setStatusText(text), time)
    );

    // Success state - green glow for 1 second
    const successTimer = setTimeout(() => {
      setStage('success');
    }, duration);

    // Complete and fade out after 1 second of green glow
    const completeTimer = setTimeout(() => {
      setStage('complete');
      if (onComplete) onComplete();
    }, duration + 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(successTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, duration]);

  const styles = {
    loadingOverlay: {
      background: 'linear-gradient(135deg, rgba(13, 17, 23, 0.95), rgba(10, 15, 25, 0.98))',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '300px',
      width: '100%',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      borderRadius: '16px',
      maxWidth: '500px',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      opacity: stage === 'complete' ? 0 : 1,
      transition: 'opacity 0.5s ease-out',
    },
    csvContainer: {
      position: 'relative',
      width: '120px',
      height: '120px',
      margin: '20px 0',
    },
    csvDocument: {
      width: '80px',
      height: '100px',
      background: stage === 'success' 
        ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)'
        : 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
      borderRadius: '8px',
      position: 'absolute',
      top: '10px',
      left: '50%',
      transform: 'translateX(-50%)',
      animation: stage !== 'complete' ? 'documentFloat 3s ease-in-out infinite' : 'none',
      boxShadow: stage === 'success'
        ? '0 8px 25px rgba(34, 197, 94, 0.3)'
        : '0 8px 20px rgba(0, 0, 0, 0.3)',
      border: stage === 'success' ? '2px solid #22c55e' : '2px solid #cbd5e1',
      transition: 'all 0.3s ease-in-out',
    },
    csvHeader: {
      position: 'absolute',
      top: '8px',
      left: '8px',
      right: '8px',
      height: '16px',
      background: stage === 'success' 
        ? 'linear-gradient(90deg, #22c55e, #16a34a)'
        : 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
      borderRadius: '3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '8px',
      fontWeight: 'bold',
      color: 'white',
      transition: 'background 0.3s ease-in-out',
    },
    csvLines: {
      position: 'absolute',
      top: '32px',
      left: '10px',
      right: '10px',
    },
    csvLine: {
      height: '3px',
      background: stage === 'success' ? '#16a34a' : '#64748b',
      marginBottom: '6px',
      borderRadius: '2px',
      animation: stage !== 'complete' ? 'lineFill 2s ease-in-out infinite' : 'none',
      transition: 'background 0.3s ease-in-out',
    },
    csvProgress: {
      position: 'absolute',
      bottom: '-30px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100px',
      height: '6px',
      background: stage === 'success'
        ? 'rgba(34, 197, 94, 0.3)'
        : 'rgba(59, 130, 246, 0.3)',
      borderRadius: '3px',
      overflow: 'hidden',
      transition: 'background 0.3s ease-in-out',
    },
    csvProgressFill: {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '100%',
      background: stage === 'success'
        ? 'linear-gradient(90deg, #22c55e, #16a34a)'
        : 'linear-gradient(90deg, #3b82f6, #60a5fa)',
      animation: stage !== 'complete' ? 'progressFill 3s ease-in-out infinite' : 'none',
      transition: 'background 0.3s ease-in-out',
    },
    statusText: {
      marginTop: '30px',
      fontSize: '16px',
      color: stage === 'success' ? '#22c55e' : '#e6edf3',
      fontWeight: '500',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      textShadow: stage === 'success' ? '0 0 10px #22c55e' : 'none',
      transition: 'all 0.3s ease-in-out',
      textAlign: 'center',
      maxWidth: '400px',
    },
    securityBadge: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: stage === 'success' 
        ? 'rgba(34, 197, 94, 0.15)'
        : 'rgba(59, 130, 246, 0.1)',
      color: stage === 'success' ? '#22c55e' : '#3b82f6',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      border: stage === 'success'
        ? '1px solid rgba(34, 197, 94, 0.4)'
        : '1px solid rgba(59, 130, 246, 0.3)',
      boxShadow: stage === 'success'
        ? '0 0 15px rgba(34, 197, 94, 0.3)'
        : '0 0 10px rgba(59, 130, 246, 0.2)',
      transition: 'all 0.3s ease-in-out',
    },
  };

  const keyframes = `
    @keyframes documentFloat {
      0%, 100% { 
        transform: translateX(-50%) translateY(0px); 
      }
      50% { 
        transform: translateX(-50%) translateY(-8px); 
      }
    }
    
    @keyframes lineFill {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
    
    @keyframes progressFill {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(0%); }
    }
  `;

  // Line widths for CSV data representation
  const lineWidths = ['85%', '65%', '90%', '45%', '75%', '55%', '80%'];

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.loadingOverlay}>
        <div style={styles.securityBadge}>
          {stage === 'success' ? 'CSV READY' : 'DATA PROCESSING'}
        </div>
        
        <div style={styles.csvContainer}>
          <div style={styles.csvDocument}>
            <div style={styles.csvHeader}>
              CSV
            </div>
            <div style={styles.csvLines}>
              {lineWidths.map((width, index) => (
                <div 
                  key={index}
                  style={{
                    ...styles.csvLine, 
                    width,
                    animationDelay: `${index * 0.2}s`
                  }}
                />
              ))}
            </div>
          </div>
          
          <div style={styles.csvProgress}>
            <div style={styles.csvProgressFill}></div>
          </div>
        </div>
        
        <div style={styles.statusText}>
          {statusText}
        </div>
      </div>
    </>
  );
};
export default CSVProcessingLoader;